// Top-level chrome — header (nav + branding) + main content area.
//
// Nav structure scales from mobile to desktop:
//   - Mobile (< md): hamburger only → slide-in drawer with all
//     sections grouped. Tap a link → close drawer + navigate.
//   - Tablet/Desktop (md+): primary tabs visible inline, secondary
//     items under hover/click dropdowns. 18 routes collapse into
//     4 primary tabs + 3 dropdown groups.
//
// Active-state highlighting flows through nested NavLinks so a
// dropdown trigger highlights when any of its children is the
// current route.

import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { clearToken, getToken } from "../lib/auth";
import { agentsApi, alertsApi } from "../lib/api";

interface NavItem {
  to: string;
  label: string;
  exact?: boolean;
  /** Optional count badge — surfaced in dropdown rows to mirror the
   *  parent group's badge so the operator can see at a glance which
   *  sub-item the count belongs to. */
  badge?: number;
}

interface NavGroup {
  label: string;
  /** Routes that should make the group label appear active. */
  match: string[];
  sections: { heading?: string; items: NavItem[] }[];
}

export function Layout() {
  const tokenSet = Boolean(getToken());
  const alertsQ = useQuery({
    queryKey: ["alerts-count"],
    queryFn: () => alertsApi.list(),
    enabled: tokenSet,
    refetchInterval: 30_000,
    staleTime: 20_000,
  });
  const openCount = alertsQ.data?.counts.total ?? 0;
  const hasCritical = (alertsQ.data?.counts.critical ?? 0) > 0;
  const pendingQ = useQuery({
    queryKey: ["agents-pending-count"],
    queryFn: () => agentsApi.pending(),
    enabled: tokenSet,
    refetchInterval: 30_000,
    staleTime: 20_000,
  });
  const pendingCount = pendingQ.data?.pending.length ?? 0;

  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  // Auto-close mobile drawer on route change.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const optimizeGroup: NavGroup = {
    label: "Optimize",
    match: ["/optimize", "/optimize/impact"],
    sections: [{
      items: [
        // exact:true so /optimize doesn't also activate when on
        // /optimize/impact (NavLink is partial-match by default).
        { to: "/optimize", label: "Targets", exact: true },
        { to: "/optimize/impact", label: "Impact" },
      ],
    }],
  };

  const agentsGroup: NavGroup = {
    label: "Agents",
    match: ["/agents", "/agents/pending", "/agents/connie", "/pipelines"],
    sections: [{
      items: [
        // exact:true so /agents doesn't also activate when on
        // /agents/pending or /agents/<id> (NavLink is partial-match
        // by default; would highlight both items at once).
        { to: "/agents", label: "Agents", exact: true },
        { to: "/agents/pending", label: "Pending approvals", badge: pendingCount },
        { to: "/agents/connie", label: "Connie's log" },
        { to: "/pipelines", label: "System flows" },
      ],
    }],
  };

  const operationsGroup: NavGroup = {
    label: "Operations",
    match: [
      "/leads", "/outreach", "/journo", "/ops/prospects",
      "/citations", "/testimonials", "/ops/twilio",
      "/clients", "/ops/finance", "/ops",
      "/markets/gaps", "/integrations",
      "/reports/executive-seo",
    ],
    sections: [
      {
        heading: "Leads & outreach",
        items: [
          { to: "/leads", label: "Leads" },
          { to: "/outreach", label: "Outreach" },
          { to: "/journo", label: "Journo" },
          { to: "/ops/prospects", label: "Prospects" },
        ],
      },
      {
        heading: "Listings",
        items: [
          { to: "/citations", label: "Citations" },
          { to: "/testimonials", label: "Reviews" },
          { to: "/ops/twilio", label: "Phones" },
        ],
      },
      {
        heading: "Expansion",
        items: [
          { to: "/markets/gaps", label: "Market gaps" },
        ],
      },
      {
        heading: "Money & ops",
        items: [
          { to: "/clients", label: "Clients" },
          { to: "/ops/finance", label: "Finance" },
          { to: "/ops", label: "Ops console" },
          { to: "/integrations", label: "Integrations" },
        ],
      },
      {
        heading: "Reports",
        items: [
          { to: "/reports/executive-seo", label: "Executive SEO" },
        ],
      },
    ],
  };

  return (
    <div className="min-h-full flex flex-col">
      <header className="bg-white border-b border-ink-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          {/* Brand */}
          <Link to="/" className="flex items-baseline gap-2 no-underline shrink-0">
            <span className="text-xl font-display font-bold text-ink-900">RankForge</span>
            <span className="hidden sm:inline text-xs uppercase tracking-wider text-ink-500">Operator</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1 lg:gap-2 text-sm flex-1 justify-center">
            <NavTab to="/" label="Home" exact />
            <NavTab to="/inbox" label="Inbox" badge={openCount} badgeUrgent={hasCritical} />
            <NavTab to="/sites" label="Sites" />
            <NavTab to="/content" label="Content" />
            <NavTab to="/momentum" label="Momentum" />
            <NavDropdown group={optimizeGroup} />
            <NavDropdown group={agentsGroup} badge={pendingCount} />
            <NavDropdown group={operationsGroup} />
          </nav>

          {/* Right side: sign out + mobile hamburger */}
          <div className="flex items-center gap-2 shrink-0">
            {tokenSet && (
              <button
                onClick={() => { clearToken(); window.location.href = "/login"; }}
                className="hidden md:inline text-xs text-ink-500 hover:text-ink-900 font-medium"
              >Sign out</button>
            )}
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-md text-ink-700 hover:bg-ink-100 relative"
              aria-label="Open navigation menu"
            >
              <HamburgerIcon />
              {(openCount > 0 || pendingCount > 0) && (
                <span className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${hasCritical ? "bg-red-500" : "bg-amber-500"}`} />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <MobileDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        primaryItems={[
          { to: "/", label: "Home", exact: true },
          { to: "/inbox", label: "Inbox", badge: openCount, badgeUrgent: hasCritical },
          { to: "/sites", label: "Sites" },
          { to: "/momentum", label: "Momentum" },
        ]}
        groups={[
          optimizeGroup,
          { ...agentsGroup, badge: pendingCount },
          operationsGroup,
        ]}
        onSignOut={tokenSet ? () => { clearToken(); window.location.href = "/login"; } : null}
      />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}

// ─── primary nav tab (top-level link) ─────────────────────────────

function NavTab({
  to,
  label,
  exact,
  badge,
  badgeUrgent,
}: {
  to: string;
  label: string;
  exact?: boolean;
  badge?: number;
  badgeUrgent?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        "px-3 py-1.5 rounded-md font-medium transition-colors inline-flex items-center gap-1.5 whitespace-nowrap " +
        (isActive
          ? "bg-ink-900 text-white"
          : "text-ink-600 hover:text-ink-900 hover:bg-ink-100")
      }
    >
      <span>{label}</span>
      {typeof badge === "number" && badge > 0 && (
        <span
          className={
            "text-[10px] leading-none px-1.5 py-0.5 rounded-full font-bold " +
            (badgeUrgent ? "bg-red-500 text-white" : "bg-amber-500 text-white")
          }
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}

// ─── desktop dropdown ──────────────────────────────────────────────

function NavDropdown({
  group,
  badge,
}: {
  group: NavGroup;
  badge?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isActive = group.match.some((m) =>
    location.pathname === m || location.pathname.startsWith(m + "/")
  );

  // Click-outside to close. Captures both pointerdown (faster than
  // click) and Escape key for keyboard users.
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("pointerdown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);
  // Auto-close on route change.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={
          "px-3 py-1.5 rounded-md font-medium transition-colors inline-flex items-center gap-1.5 whitespace-nowrap " +
          (isActive
            ? "bg-ink-900 text-white"
            : "text-ink-600 hover:text-ink-900 hover:bg-ink-100")
        }
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span>{group.label}</span>
        {typeof badge === "number" && badge > 0 && (
          <span className="text-[10px] leading-none px-1.5 py-0.5 rounded-full font-bold bg-amber-500 text-white">
            {badge}
          </span>
        )}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={`transition-transform ${open ? "rotate-180" : ""}`}>
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full right-0 mt-1.5 w-56 bg-white rounded-lg shadow-lg border border-ink-100 py-2 z-40"
        >
          {group.sections.map((section, idx) => (
            <div key={idx} className={idx > 0 ? "mt-2 pt-2 border-t border-ink-100" : ""}>
              {section.heading && (
                <div className="px-3 pb-1 text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
                  {section.heading}
                </div>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  // Always exact-match in dropdowns: a parent like
                  // /agents would otherwise also match /agents/pending,
                  // causing both items to highlight at once.
                  end
                  className={({ isActive }) =>
                    "px-3 py-1.5 text-sm transition-colors flex items-center justify-between gap-2 " +
                    (isActive
                      ? "bg-ink-900/5 text-ink-900 font-medium"
                      : "text-ink-700 hover:bg-ink-50")
                  }
                  onClick={() => setOpen(false)}
                >
                  <span>{item.label}</span>
                  {typeof item.badge === "number" && item.badge > 0 && (
                    <span className="text-[10px] leading-none px-1.5 py-0.5 rounded-full font-bold bg-amber-500 text-white">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── mobile drawer ─────────────────────────────────────────────────

function MobileDrawer({
  open,
  onClose,
  primaryItems,
  groups,
  onSignOut,
}: {
  open: boolean;
  onClose: () => void;
  primaryItems: Array<{ to: string; label: string; exact?: boolean; badge?: number; badgeUrgent?: boolean }>;
  groups: Array<NavGroup & { badge?: number }>;
  onSignOut: (() => void) | null;
}) {
  // Lock body scroll while drawer open + ESC to close.
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={
          "fixed inset-0 bg-ink-900/40 z-40 transition-opacity md:hidden " +
          (open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none")
        }
        onClick={onClose}
        aria-hidden={!open}
      />
      {/* Panel */}
      <aside
        className={
          "fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white z-50 shadow-2xl transition-transform md:hidden flex flex-col " +
          (open ? "translate-x-0" : "translate-x-full")
        }
        aria-hidden={!open}
      >
        <div className="px-4 py-3 border-b border-ink-100 flex items-center justify-between shrink-0">
          <span className="text-sm font-display font-bold text-ink-900">Menu</span>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-md text-ink-700 hover:bg-ink-100 flex items-center justify-center"
            aria-label="Close menu"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3 3l12 12M15 3L3 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {/* Primary tier */}
          <div className="px-2 pb-1 text-[10px] uppercase tracking-wider text-ink-400 font-semibold">Primary</div>
          {primaryItems.map((p) => (
            <DrawerLink key={p.to} {...p} />
          ))}
          {/* Groups */}
          {groups.map((g) => (
            <div key={g.label} className="mt-4">
              <div className="px-2 pb-1 text-[10px] uppercase tracking-wider text-ink-400 font-semibold flex items-center justify-between">
                <span>{g.label}</span>
                {typeof g.badge === "number" && g.badge > 0 && (
                  <span className="text-[10px] leading-none px-1.5 py-0.5 rounded-full font-bold bg-amber-500 text-white">
                    {g.badge}
                  </span>
                )}
              </div>
              {g.sections.map((section, sidx) => (
                <div key={sidx}>
                  {section.heading && (
                    <div className="px-2 pt-2 pb-0.5 text-[10px] text-ink-500">{section.heading}</div>
                  )}
                  {section.items.map((item) => <DrawerLink key={item.to} {...item} />)}
                </div>
              ))}
            </div>
          ))}
        </nav>
        {onSignOut && (
          <div className="px-3 py-3 border-t border-ink-100 shrink-0">
            <button
              onClick={onSignOut}
              className="text-xs text-ink-500 hover:text-ink-900 font-medium"
            >
              Sign out
            </button>
          </div>
        )}
      </aside>
    </>
  );
}

function DrawerLink({
  to,
  label,
  exact,
  badge,
  badgeUrgent,
}: {
  to: string;
  label: string;
  exact?: boolean;
  badge?: number;
  badgeUrgent?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        "block px-3 py-2.5 text-sm rounded-md flex items-center justify-between transition-colors " +
        (isActive
          ? "bg-ink-900 text-white font-medium"
          : "text-ink-700 hover:bg-ink-100")
      }
    >
      <span>{label}</span>
      {typeof badge === "number" && badge > 0 && (
        <span
          className={
            "text-[10px] leading-none px-1.5 py-0.5 rounded-full font-bold " +
            (badgeUrgent ? "bg-red-500 text-white" : "bg-amber-500 text-white")
          }
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}

// ─── icons ──────────────────────────────────────────────────────────

function HamburgerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}
